<script setup lang="ts">
import { computed, defineProps, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import shared from '@/views/manage-content/media/shared';
import store from "@/store";

const { removeItemGetConfirm, removeSticker, approveSticker } = shared();

const route = useRoute();
const router = useRouter();
const item = store.getters['sticker/GET_STICKER'];
const isOrgAdmin = computed(() => store.getters['users/isOrgAdmin']);
const selectedOrganization = computed(() => store.getters['organizations/GET_SELECTED_ORGANIZATION']);
const { id } = route.params;

// Loading states for each button
const isDeleteLoading = ref(false);
const isDenyLoading = ref(false);
const isApproveLoading = ref(false);

const props = defineProps<{
  isEditPage: boolean;
  isAdmin: boolean;
  stickerType: string;
  isActive: boolean;
  fetchSticker: () => Promise<void>;
}>();

const { isEditPage, fetchSticker } = props;

function removeItem() {
  if (isDeleteLoading.value) return;

  removeItemGetConfirm(async () => {
    isDeleteLoading.value = true;
    try {
      await removeSticker(item.id);
      router.push('/manage-content/media/stickers');
    } finally {
      isDeleteLoading.value = false;
    }
  });
}

async function publishItem(opt) {
  if (opt === false && isDenyLoading.value) return;
  if (opt === true && isApproveLoading.value) return;

  let status = 'Active';
  if (opt === false) {
    status = 'Archived';
    isDenyLoading.value = true;
  } else {
    isApproveLoading.value = true;
  }

  try {
    await approveSticker({
      id,
      status,
    });
    await fetchSticker();
  } finally {
    if (opt === false) {
      isDenyLoading.value = false;
    } else {
      isApproveLoading.value = false;
    }
  }
}

const redirectToStickersPage = async () => {
  if (isOrgAdmin.value) {
    if (selectedOrganization && selectedOrganization.value && selectedOrganization.value.id) {
      return await router.push(`/manage/orgs/${selectedOrganization.value.id}/stickers`);
    }
    return router.push('/manage/orgs');
  }
  return router.push('/manage-content/media/stickers');
};

</script>

<template>
  <article class="c-breadcrumb-actions">
    <section class="c-add-sticker__breadcrumb" @click="redirectToStickersPage">
      <fai height="40px" icon="arrow-left" type="fas" width="40px" />
      <h1 class="c-breadcrumb-actions__title">{{ isEditPage ? 'Edit ' : 'Add ' }}Sticker</h1>
    </section>

    <section v-if="isEditPage && isAdmin">
      <el-button
        type="danger"
        plain
        @click="removeItem"
        class="has-fai"
        :disabled="isDeleteLoading"
        :loading="isDeleteLoading"
      >
        <fai type="fal" icon="trash" class="size-18 me-2" />
        <span class="min-w-110 d-inline-block">Delete</span>
      </el-button>

      <el-button
        v-if="!item.seen || (item.seen && item.published)"
        type="danger"
        @click="publishItem(false)"
        class="has-fai"
        :disabled="isDenyLoading"
        :loading="isDenyLoading"
      >
        <fai type="fal" icon="times-circle" class="size-18 me-2" />
        <span class="min-w-110 d-inline-block">Deny</span>
      </el-button>
      <el-button
        v-if="!item.seen || (item.seen && !item.published)"
        type="success"
        :style="{ color: '#1EBEAF' }"
        @click="publishItem(true)"
        class="has-fai"
        :disabled="isApproveLoading"
        :loading="isApproveLoading"
      >
        <fai type="fal" icon="check-circle" class="size-18 me-2" style="color: #fff" />
        <span class="min-w-110 d-inline-block text-white">Approve</span>
      </el-button>
    </section>

  </article>
</template>

<style scoped lang="scss">
.c-breadcrumb-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;

  &__title {
    color: black;
    font-size: 40px;
    margin: 0 0 0 15px;
    font-weight: 900;
  }

}
</style>
